import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
} from "@mui/material";
import { voiceAgentService } from "../../../../services/VoiceAgentService";
import { ArrowForward } from "@mui/icons-material";

const Fold3 = () => {
  const [selectedCountry, setSelectedCountry] = useState("+1");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [callInitiated, setCallInitiated] = useState(false);
  const [isPhoneCallLoading, setIsPhoneCallLoading] = useState(false);
  const [callStatus, setCallStatus] = useState("");

  const handlePhoneCall = async (useCaseId = "sales-assistant") => {
    if (!phoneNumber) {
      alert("Please enter a phone number");
      return;
    }

    setIsPhoneCallLoading(true);
    setCallInitiated(false);
    setCallStatus("");

    try {
      const fullPhoneNumber = `${selectedCountry}${phoneNumber}`;
      const result = await voiceAgentService.makeOutboundCall(
        fullPhoneNumber,
        useCaseId
      );

      if (result.status === "started") {
        setCallInitiated(true);
        setCallStatus(`Outbound ${useCaseId} call initiated successfully`);
      } else {
        throw new Error("Call failed to start");
      }
    } catch (error) {
      console.error("Error making outbound call:", error);
      setCallStatus("Failed to initiate outbound call");
      alert("Failed to start outbound call. Please try again.");
    } finally {
      setIsPhoneCallLoading(false);
    }
  };

  const containerStyle = {
    minHeight: "80vh",
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    padding: "80px 0",
  };

  const wrapperStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 20px",
    width: "100%",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "60px",
    alignItems: "stretch", // Changed from center to stretch
  };

  const leftColumnStyle = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };

  const stepsContainerStyle = {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "center",
  };

  const stepStyle = {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    padding: "24px",
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    border: "1px solid rgba(234, 99, 71, 0.1)",
    transition: "all 0.3s ease",
    cursor: "pointer",
  };

  const stepNumberStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "50px",
    height: "50px",
    background: "linear-gradient(135deg, #ea6347, #ff6b4a)",
    color: "white",
    borderRadius: "50%",
    fontSize: "1.5rem",
    fontWeight: "700",
    flexShrink: 0,
  };

  const stepIconStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "60px",
    height: "60px",
    background:
      "linear-gradient(135deg, rgba(234, 99, 71, 0.1), rgba(234, 99, 71, 0.05))",
    borderRadius: "50%",
    flexShrink: 0,
    fontSize: "2rem",
  };

  const connectorStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "16px 0",
  };

  const connectorLineStyle = {
    width: "2px",
    height: "20px",
    background: "linear-gradient(180deg, #ea6347, rgba(234, 99, 71, 0.3))",
    marginBottom: "4px",
  };

  const demoBoxStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "20px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(234, 99, 71, 0.1)",
    height: "60%", // Make it take full height
    minHeight: "500px", // Set minimum height
  };

  const rightColumnStyle = {
    display: "flex",
    alignItems: "center",
    height: "100%",
  };

  const responsiveStyle = `
    @media (max-width: 1024px) {
      .grid-container {
        grid-template-columns: 1fr !important;
        gap: 40px !important;
      }
      .demo-box {
        min-height: auto !important;
      }
    }
    @media (max-width: 768px) {
      .step-item {
        flex-direction: column !important;
        text-align: center !important;
        gap: 16px !important;
        padding: 20px !important;
      }
      .main-container {
        padding: 40px 0 !important;
      }
      .section-title {
        font-size: 2rem !important;
      }
      .demo-title {
        font-size: 1.5rem !important;
      }
      .demo-box {
        min-height: auto !important;
      }
    }
  `;

  const renderPhoneCallSection = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        <FormControl sx={{ minWidth: "80px" }}>
          <Select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            size="small"
          >
            <MenuItem value="+1">🇺🇸 +1</MenuItem>
            <MenuItem value="+44">🇬🇧 +44</MenuItem>
            <MenuItem value="+91">🇮🇳 +91</MenuItem>
            <MenuItem value="+966">🇸🇦 +966</MenuItem>
            <MenuItem value="+86">🇨🇳 +86</MenuItem>
            <MenuItem value="+49">🇩🇪 +49</MenuItem>
            <MenuItem value="+33">🇫🇷 +33</MenuItem>
            <MenuItem value="+81">🇯🇵 +81</MenuItem>
            <MenuItem value="+61">🇦🇺 +61</MenuItem>
            <MenuItem value="+55">🇧🇷 +55</MenuItem>
            <MenuItem value="+7">🇷🇺 +7</MenuItem>
          </Select>
        </FormControl>
        <TextField
          type="tel"
          placeholder="Enter phone number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
        />
      </Box>

      <Button
        variant="contained"
        size="large"
        endIcon={<ArrowForward />}
        onClick={() => handlePhoneCall("sales-assistant")}
        disabled={isPhoneCallLoading || !phoneNumber}
        sx={{
          backgroundColor: "#ea6347",
          "&:hover": { backgroundColor: "#d55640" },
          "&:disabled": { backgroundColor: "#ccc" },
          padding: "12px 24px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        {isPhoneCallLoading ? "Calling..." : "Talk Now"}
      </Button>

      {callInitiated && (
        <Typography
          sx={{
            color: "#4caf50",
            fontSize: "0.875rem",
            textAlign: "center",
            marginTop: "8px",
            fontWeight: "500",
          }}
        >
          You should be receiving a call right now.
        </Typography>
      )}

      {callStatus && !callInitiated && (
        <Typography
          sx={{
            color: "#f44336",
            fontSize: "0.875rem",
            textAlign: "center",
            marginTop: "8px",
            fontWeight: "500",
          }}
        >
          {callStatus}
        </Typography>
      )}

      <Typography
        sx={{
          color: "#666",
          fontSize: "0.875rem",
          textAlign: "center",
          maxWidth: "400px",
        }}
      >
        Don't wait for the demo. Just enter your number and receive a call from
        our agent now.
      </Typography>
    </Box>
  );

  return (
    <div style={containerStyle} className="main-container">
      <style>{responsiveStyle}</style>
      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        .step-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }
        .bounce-arrow {
          animation: bounce 2s ease-in-out infinite;
        }
      `}</style>

      <div style={wrapperStyle}>
        <div style={{ marginBottom: "50px" }}>
          <h2 style={{ textAlign: "center" }} className="section-title">
            How It Works
          </h2>
        </div>

        <div style={gridStyle} className="grid-container">
          {/* Left Column - 3 Step Flow */}
          <div style={leftColumnStyle}>
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
              <p className="section-subtitle">Simple 3-Step Flow</p>
            </div>

            <div style={stepsContainerStyle} className="steps-container">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3 className="step-title">Trigger Call</h3>
                  <p className="step-description">
                    Instantly after cart abandonment (within 30 min–2 hr), our
                    AI calls the shopper, mentions their name + products.
                  </p>
                </div>
                <div className="step-icon">
                  <span className="icon">📞</span>
                </div>
              </div>

              <div className="step-connector">
                <div className="connector-line"></div>
                <div className="connector-arrow">↓</div>
              </div>

              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3 className="step-title">Live Conversation</h3>
                  <p className="step-description">
                    AI agent asks if there were issues, answers questions, and
                    offers incentives (e.g.,10–15% off).
                  </p>
                </div>
                <div className="step-icon">
                  <span className="icon">💬</span>
                </div>
              </div>

              <div className="step-connector">
                <div className="connector-line"></div>
                <div className="connector-arrow">↓</div>
              </div>

              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3 className="step-title">Send SMS</h3>
                  <p className="step-description">
                    During the call, an SMS with a checkout link and discount
                    code goes out—seamlessly moving them back to buy.
                  </p>
                </div>
                <div className="step-icon">
                  <span className="icon">📱</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Demo Section */}
          <div style={rightColumnStyle}>
            <div style={demoBoxStyle} className="demo-box">
              <h3 className="step-title">Demo Shopify Cart Recovery Agent</h3>
              <p
                style={{
                  textAlign: "center",
                }}
                className="step-description"
              >
                Intelligent voice agents that recover abandoned carts and guide
                customers to complete their purchases.
              </p>

              {/* Phone Call Section */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  marginTop: "24px",
                  width: "100%",
                }}
              >
                {renderPhoneCallSection()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fold3;
